import { isEmpty, isNumber, get } from 'lodash';

import {
  isAbGroupNode,
  isLoopAndMergeGroupNode,
  isConjointOrMaxDiffGroupNode,
  isVanWestendorpGroupNode,
  isVanWestendorpQuestionNode,
} from 'utils/nodeTypes';

import {
  hasConjointEmptyAttributeValues,
  hasMixedPositiveWithNegativePriceValues,
  hasSameAttributePriceValues,
} from 'containers/QuestionnairePage/QuestionGroup/utils';
import { CONJOINT_ATTRIBUTE_TYPES } from 'containers/QuestionnairePage/QuestionGroup/Conjoint/constants';
import {
  hasExceededMaxAttributeValuesLimit,
  getMaxAttributeValuesLimit,
} from 'containers/QuestionnairePage/QuestionGroup/Conjoint/utils';
import { hasEmptyHtml } from 'components/SurveySettings/utils';
import {
  getMaxLoopAndMergeRepetitions,
  isIncompleteCarryForward,
} from '../NodeSettings/settings/utils/CarryForward';
import { FORWARD_ANSWER_AS } from '../NodeSettings/settings/utils/constants';
import { getPreviousNodeRefIdsForNode } from '../GenericQuestion/SingleMultipleQuestion/utils';

function validate(
  node,
  nodes = [],
  previousNodesPerNode = {},
  screeningNodes = [],
) {
  const errors = {};

  if (node.questions.length === 0) {
    errors.emptyGroup = true;
  }

  if (isAbGroupNode(node)) {
    const { attributes, properties, concepts } = node;
    const emptyAttributeContent = attributes.some((attribute) =>
      attribute.values.some(
        ({ media, text }) => isEmpty(media) && isEmpty(text),
      ),
    );

    if (emptyAttributeContent) {
      errors.emptyAttributeContent = true;
    }

    if (properties.repetitions > concepts.length) {
      errors.repetitionsBiggerThanConceptsLength = true;
    }
  }

  if (isLoopAndMergeGroupNode(node)) {
    const {
      attributes,
      properties: { repetitions = -Infinity } = {},
      loopAndMergeFromQuestionConfiguration = {},
    } = node;
    if (
      attributes.some((attribute) =>
        attribute.values.some((value) => hasEmptyHtml(value.text)),
      )
    ) {
      errors.missingVariables = true;
    }
    if (attributes.some((attribute) => isEmpty(attribute.name))) {
      errors.missingVariableNames = true;
    }

    if (!isEmpty(loopAndMergeFromQuestionConfiguration)) {
      // Validate L&M from questions configuration is valid by checking that source questions is preceding the loop and merge question (through ADL).
      // Also validate that it is available for selection (other filters like no CF, correct type etc).
      const previousNodes = getPreviousNodeRefIdsForNode({
        refId: node.refId,
        previousNodesPerNode,
        nodes,
        screeningNodes,
      });

      let hasInvalidSourceQuestion = false;
      const hasProfilingConfiguration =
        loopAndMergeFromQuestionConfiguration.isProfiling;
      if (!hasProfilingConfiguration) {
        hasInvalidSourceQuestion = !previousNodes.includes(
          loopAndMergeFromQuestionConfiguration.questionRefId,
        );
      }
      if (hasInvalidSourceQuestion) {
        errors.loopAndMergeConfigInvalidSourceQuestion = true;
      }

      if (hasProfilingConfiguration && nodes.length === 1) {
        errors.loopAndMergeOnlyQuestionWithAudienceConfig = true;
      }

      // Validate that L&M configuration from question is complete.
      if (
        !isEmpty(loopAndMergeFromQuestionConfiguration) &&
        isIncompleteCarryForward(
          {
            ...loopAndMergeFromQuestionConfiguration,
            answerAs: FORWARD_ANSWER_AS.ANSWERS,
          },
          nodes,
        )
      ) {
        errors.loopAndMergeConfigIncomplete = true;
      }
    }
    //

    // Validate repetitions value is within limits (<= max answers that can be provided by config)
    const maxAvailableRepetitions = getMaxLoopAndMergeRepetitions(node, nodes);
    if (repetitions < 1 || repetitions > maxAvailableRepetitions) {
      errors.loopAndMergeRepetitionsOutOfLimits = true;
    }
  }

  if (isConjointOrMaxDiffGroupNode(node)) {
    const { attributes, properties, questions = [] } = node;

    if (questions[0] && isEmpty(questions[0].prompt)) {
      errors.missingPrompt = true;
    }

    const emptyAttributeContent = attributes.some((attribute) =>
      isEmpty(attribute.name),
    );

    if (emptyAttributeContent) {
      errors.emptyAttributeContent = true;
    }

    const emptyAttributeValues = attributes.some((attribute) =>
      hasConjointEmptyAttributeValues(attribute.values, attribute.type),
    );

    if (emptyAttributeValues) {
      errors.emptyAttributeValues = true;
    }

    const maxAttributeValues = getMaxAttributeValuesLimit(attributes.length);
    const exceededMaxAttributeValues = attributes.some((attribute) =>
      hasExceededMaxAttributeValuesLimit(attribute.values, maxAttributeValues),
    );

    if (exceededMaxAttributeValues) {
      errors.exceededMaxAttributeValues = true;
    }

    const mixedPositiveWithNegativePriceValues = attributes.some((attribute) =>
      hasMixedPositiveWithNegativePriceValues(attribute.values, attribute.type),
    );

    if (mixedPositiveWithNegativePriceValues) {
      errors.mixedPositiveWithNegativePriceValues = true;
    }

    const sameAttributePriceValues = attributes.some((attribute) =>
      hasSameAttributePriceValues(attribute.values, attribute.type),
    );

    if (sameAttributePriceValues) {
      errors.sameAttributePriceValues = true;
    }

    const emptyOrOneLevel = properties.excludedCombinations.some(
      (combination) => combination.length < 2,
    );

    if (emptyOrOneLevel) {
      errors.emptyOrOneLevelInExcludedCombination = true;
    }

    const brandTypeAttributes = attributes.filter(
      ({ type }) => type === CONJOINT_ATTRIBUTE_TYPES.BRAND,
    );
    const priceTypeAttributes = attributes.filter(
      ({ type }) => type === CONJOINT_ATTRIBUTE_TYPES.PRICE,
    );

    if (brandTypeAttributes.length > 1 && priceTypeAttributes.length > 1) {
      errors.brandOrPriceTypeAttributesMoreThanOne = true;
    } else if (brandTypeAttributes.length > 1) {
      errors.brandTypeAttributesMoreThanOne = true;
    } else if (priceTypeAttributes.length > 1) {
      errors.priceTypeAttributesMoreThanOne = true;
    }
  }

  if (isVanWestendorpGroupNode(node)) {
    const vanWestendorpQuestion = node.questions.find(
      isVanWestendorpQuestionNode,
    );
    const vanWestendorpSettings = get(
      vanWestendorpQuestion,
      'matrix.validation.vanWestendorpValidationDto',
      {},
    );
    const { decimalCount, min, max } = vanWestendorpSettings;

    const isNumberMin = isNumber(min);
    const isNumberMax = isNumber(max);

    // wrong limit values
    if (isNumberMin && isNumberMax && max <= min) {
      errors.limitMaxSmaller = true;
    }

    // wrong decimal values
    if (decimalCount === 0) {
      errors.decimalsZero = true;
    } else if (isNumber(decimalCount) && decimalCount < 0) {
      errors.decimalsInvalid = true;
    }
  }
  return errors;
}

export default validate;
